import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useQuery, useMutation } from '@apollo/client';
import {
  PlusIcon,
  TrashIcon,
  RocketLaunchIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useLayoutContext } from '~/components/layout/Layout';

// GraphQL queries and mutations
const GET_CAMPAIGNS_BY_AGENT = gql`
  query GetCampaignsByAgent($data: GetCampaignsByAgentInputType!) {
    getCampaignsByAgent(data: $data) {
      data
      hasError
      message
    }
  }
`;

const GET_AUDIENCES_BY_AGENT = gql`
  query GetAudiencesByAgent($data: GetAudiencesByAgentInputType!) {
    getAudiencesByAgent(data: $data) {
      data
      hasError
      message
    }
  }
`;

const LIST_WHATSAPP_TEMPLATES = gql`
  query ListWhatsappTemplates($data: ListWhatsappTemplatesInputType!) {
    list_whatsapp_templates(data: $data) {
      data
      hasError
      message
    }
  }
`;

const CREATE_CAMPAIGN = gql`
  mutation CreateCampaign($data: CreateCampaignInputType!) {
    createCampaign(data: $data) {
      data
      hasError
      message
    }
  }
`;

const EXECUTE_CAMPAIGN = gql`
  mutation ExecuteCampaign($campaign_id: String!) {
    executeCampaign(campaign_id: $campaign_id) {
      data
      hasError
      message
    }
  }
`;

const DELETE_CAMPAIGN = gql`
  mutation DeleteCampaign($campaign_id: String!) {
    deleteCampaign(campaign_id: $campaign_id) {
      data
      hasError
      message
    }
  }
`;

const SEND_CAMPAIGN = gql`
  mutation SendCampaign($data: SendCampaignInputType!) {
    send_campaign(data: $data) {
      data
      hasError
      message
    }
  }
`;

interface Template {
  _id: string;
  template_name?: string;
  meta_template_name?: string;
  status: string;
  language?: string;
  template?: {
    tbit_name?: string;
    language?: string;
    components: Array<{
      type: string;
      text?: string;
      format?: string;
    }>;
    variables?: Array<{
      name: string;
      position: number;
      example: string;
      component_type: string;
    }>;
  };
  variables?: Array<{
    name: string;
    position: number;
    example: string;
    component_type: string;
  }>;
}

interface Audience {
  _id: string;
  name: string;
  description?: string;
  type: string;
  estimated_size?: number;
}

interface Campaign {
  _id: string;
  name: string;
  description?: string;
  type: string;
  status: string;
  templates: Array<{
    template_id: string;
    template_name: string;
  }>;
  audience_id?: string;
  metrics?: {
    total_recipients: number;
    sent: number;
    delivered: number;
    failed: number;
  };
  created_at: string;
}

interface Contact {
  phone_number: string;
  variables: string[];
}

interface CampaignsModuleProps {
  agentId: string;
  channel: any;
}

export const CampaignsModule = ({ agentId, channel }: CampaignsModuleProps) => {
  const [t] = useTranslation('campaigns');
  const { renderToast } = useLayoutContext();

  // State
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null);
  const [showSendConfirmation, setShowSendConfirmation] = useState(false);
  const [showExecuteConfirmation, setShowExecuteConfirmation] = useState<string | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);

  // Campaign creation state
  const [campaignName, setCampaignName] = useState('');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null,
  );
  const [selectedAudience, setSelectedAudience] = useState<Audience | null>(
    null,
  );
  const [campaignType, setCampaignType] = useState<'MARKETING' | 'UTILITY'>(
    'MARKETING',
  );

  // Quick send state
  const [quickSendContacts, setQuickSendContacts] = useState<Contact[]>([]);
  // Batch size for sending to avoid Meta blocking when many messages are sent at once
  const [sendBatchSize, setSendBatchSize] = useState<number>(75);
  const [isCreating, setIsCreating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // GraphQL hooks
  const {
    data: campaignsData,
    loading: campaignsLoading,
    refetch: refetchCampaigns,
  } = useQuery(GET_CAMPAIGNS_BY_AGENT, {
    variables: {
      data: {
        agent_id: agentId,
        filters: { limit: 50 },
      },
    },
  });

  const { data: audiencesData } = useQuery(GET_AUDIENCES_BY_AGENT, {
    variables: {
      data: {
        agent_id: agentId,
        filters: { limit: 50 },
      },
    },
  });

  const {
    data: templatesData,
    loading: templatesLoading,
    error: templatesError,
  } = useQuery(LIST_WHATSAPP_TEMPLATES, {
    variables: {
      data: {
        agent_id: agentId,
        // Removed channel_id to match working TemplatesModule pattern
      },
    },
  });

  // Debug GraphQL query
  console.log('🔍 GraphQL Query debugging:');
  console.log('Templates Query Variables:', { agent_id: agentId });
  console.log('Templates Loading:', templatesLoading);
  console.log('Templates Error:', templatesError);
  console.log('Channel object:', channel);

  const [createCampaign] = useMutation(CREATE_CAMPAIGN);
  const [executeCampaign] = useMutation(EXECUTE_CAMPAIGN);
  const [deleteCampaign] = useMutation(DELETE_CAMPAIGN);
  const [sendCampaign] = useMutation(SEND_CAMPAIGN);

  // Parse data
  const campaigns: Campaign[] = campaignsData?.getCampaignsByAgent?.data || [];
  const audiences: Audience[] = audiencesData?.getAudiencesByAgent?.data || [];

  // Debug template loading
  console.log('🔍 Template debugging:');
  console.log('templatesData:', templatesData);
  console.log(
    'templatesData?.list_whatsapp_templates:',
    templatesData?.list_whatsapp_templates,
  );
  console.log(
    'templatesData?.list_whatsapp_templates?.data:',
    templatesData?.list_whatsapp_templates?.data,
  );

  // Simplified template parsing - match TemplatesModule approach
  const responseData = templatesData?.list_whatsapp_templates?.data;
  const templates: Template[] = responseData?.templates || [];

  console.log('templates array:', templates);
  console.log('templates length:', templates.length);

  // Get approved templates only
  const approvedTemplates = templates.filter((t) => t.status === 'APPROVED');
  console.log('approvedTemplates:', approvedTemplates);
  console.log('approvedTemplates length:', approvedTemplates.length);

  // Helper function to get template display name
  const getTemplateDisplayName = (template: Template): string => {
    return (
      template.template?.tbit_name ||
      template.meta_template_name ||
      template.template_name ||
      'Template sin nombre'
    );
  };

  // Helper function to get template language
  const getTemplateLanguage = (template: Template): string => {
    return template.template?.language || template.language || 'es';
  };

  // Helper function to get template variables
  const getTemplateVariables = (template: Template) => {
    return template.variables || template.template?.variables || [];
  };

  // Add contact for quick send
  const addQuickSendContact = () => {
    const templateVariables = selectedTemplate
      ? getTemplateVariables(selectedTemplate)
      : [];
    setQuickSendContacts([
      ...quickSendContacts,
      {
        phone_number: '',
        variables: new Array(templateVariables.length).fill(''),
      },
    ]);
  };

  // Update quick send contact
  const updateQuickSendContact = (
    index: number,
    field: 'phone_number' | 'variables',
    value: string | string[],
  ) => {
    const updated = [...quickSendContacts];
    if (field === 'phone_number') {
      updated[index].phone_number = value as string;
    } else {
      updated[index].variables = value as string[];
    }
    setQuickSendContacts(updated);
  };

  // Update specific variable for contact
  const updateContactVariable = (
    contactIndex: number,
    variableIndex: number,
    value: string,
  ) => {
    const updated = [...quickSendContacts];
    updated[contactIndex].variables[variableIndex] = value;
    setQuickSendContacts(updated);
  };

  // Remove contact
  const removeQuickSendContact = (index: number) => {
    setQuickSendContacts(quickSendContacts.filter((_, i) => i !== index));
  };

  // Create campaign
  const handleCreateCampaign = async () => {
    if (!campaignName.trim()) {
      renderToast({
        message: 'El nombre de la campaña es requerido',
        type: 'warning',
      });
      return;
    }

    if (!selectedTemplate) {
      renderToast({
        message: 'Debes seleccionar una plantilla',
        type: 'warning',
      });
      return;
    }

    if (!selectedAudience) {
      renderToast({
        message: 'Debes seleccionar una audiencia',
        type: 'warning',
      });
      return;
    }

    setIsCreating(true);

    try {
      const result = await createCampaign({
        variables: {
          data: {
            agent_id: agentId,
            name: campaignName.trim(),
            description: campaignDescription.trim() || undefined,
            type: campaignType,
            templates: [
              {
                template_id: selectedTemplate._id,
                template_name: getTemplateDisplayName(selectedTemplate),
              },
            ],
            audience_id: selectedAudience?._id || undefined,
            delivery_schedule: {
              type: 'IMMEDIATE',
            },
            require_opt_in: true,
          },
        },
      });

      if (result.data?.createCampaign?.hasError) {
        throw new Error(result.data.createCampaign.message);
      }

      renderToast({
        message: `Campaña "${campaignName}" creada exitosamente`,
        type: 'success',
      });

      // Reset form
      setShowCreateDialog(false);
      setCampaignName('');
      setCampaignDescription('');
      setSelectedTemplate(null);
      setSelectedAudience(null);
      refetchCampaigns();
    } catch (error: any) {
      renderToast({
        message: `Error al crear campaña: ${error.message}`,
        type: 'error',
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Quick send campaign - validation only
  const handleQuickSend = async () => {
    if (!selectedTemplate) {
      renderToast({
        message: 'Debes seleccionar una plantilla',
        type: 'warning',
      });
      return;
    }

    const validContacts = quickSendContacts.filter((c) =>
      c.phone_number.trim(),
    );
    if (validContacts.length === 0) {
      renderToast({
        message: 'Debes agregar al menos un contacto válido',
        type: 'warning',
      });
      return;
    }

    if (validContacts.length > 30) {
      renderToast({
        message: 'No puedes enviar a más de 30 contactos a la vez',
        type: 'warning',
      });
      return;
    }

    // Validate variables if template has them
    const templateVariables = getTemplateVariables(selectedTemplate);
    if (templateVariables.length > 0) {
      const incompleteContacts = validContacts.some((contact) =>
        contact.variables.some((v) => !v.trim()),
      );
      if (incompleteContacts) {
        renderToast({
          message:
            'Todos los contactos deben tener todas las variables completadas',
          type: 'warning',
        });
        return;
      }
    }

    // All validations passed, show confirmation
    setShowSendConfirmation(true);
  };

  // Actual send campaign function
  const confirmSendCampaign = async () => {
    if (!selectedTemplate) return; // Safety check
    
    const validContacts = quickSendContacts.filter((c) =>
      c.phone_number.trim(),
    );

    setIsSending(true);
    setShowSendConfirmation(false);

    try {
      const result = await sendCampaign({
        variables: {
          data: {
            agent_id: agentId,
            channel_id: channel._id,
            template_id: selectedTemplate._id,
            contacts: validContacts.map((contact) => ({
              phoneNumber: contact.phone_number,
              values: contact.variables,
            })),
          },
        },
      });

      if (result.data?.send_campaign?.hasError) {
        throw new Error(result.data.send_campaign.message);
      }

      renderToast({
        message: `Campaña enviada exitosamente a ${validContacts.length} contactos`,
        type: 'success',
      });

      // Reset form
      setShowSendDialog(null);
      setSelectedTemplate(null);
      setQuickSendContacts([]);
    } catch (error: any) {
      renderToast({
        message: `Error al enviar campaña: ${error.message}`,
        type: 'error',
      });
    } finally {
      setIsSending(false);
    }
  };

  // Execute campaign - show confirmation only
  const handleExecuteCampaign = async (campaignId: string) => {
    setShowExecuteConfirmation(campaignId);
  };

  // Actual execute campaign function
  const confirmExecuteCampaign = async () => {
    if (!showExecuteConfirmation) return;
    
    const campaignId = showExecuteConfirmation;
    setIsExecuting(true);
    setShowExecuteConfirmation(null);

    try {
      const result = await executeCampaign({
        variables: { campaign_id: campaignId },
      });

      if (result.data?.executeCampaign?.hasError) {
        throw new Error(result.data.executeCampaign.message);
      }

      renderToast({
        message: 'Campaña ejecutada exitosamente',
        type: 'success',
      });

      refetchCampaigns();
    } catch (error: any) {
      renderToast({
        message: `Error al ejecutar campaña: ${error.message}`,
        type: 'error',
      });
    } finally {
      setIsExecuting(false);
    }
  };

  // Delete campaign
  const handleDeleteCampaign = async (campaignId: string) => {
    setIsDeleting(true);

    try {
      const result = await deleteCampaign({
        variables: { campaign_id: campaignId },
      });

      if (result.data?.deleteCampaign?.hasError) {
        throw new Error(result.data.deleteCampaign.message);
      }

      renderToast({
        message: 'Campaña eliminada exitosamente',
        type: 'success',
      });

      setShowDeleteDialog(null);
      refetchCampaigns();
    } catch (error: any) {
      renderToast({
        message: `Error al eliminar campaña: ${error.message}`,
        type: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (campaignsLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">
            {t('campaigns_title')}
          </h2>
          <p className="text-sm text-gray-600">
            Gestiona y envía campañas de WhatsApp
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowSendDialog('quick')}
            className="btn btn-outline btn-sm"
          >
            <RocketLaunchIcon className="mr-2 h-4 w-4" />
            Envío Rápido
          </button>
          <button
            onClick={() => setShowCreateDialog(true)}
            className="btn btn-primary btn-sm rounded-full"
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            Nueva Campaña
          </button>
        </div>
      </div>

      {/* Campaigns List */}
      {campaigns.length > 0 ? (
        <div className="grid gap-4">
          {campaigns.map((campaign) => (
            <div
              key={campaign._id}
              className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{campaign.name}</h3>
                  {campaign.description && (
                    <p className="text-sm text-gray-600">
                      {campaign.description}
                    </p>
                  )}
                  <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                    <span>Tipo: {campaign.type}</span>
                    <span>Estado: {campaign.status}</span>
                    <span>
                      Creada:{' '}
                      {new Date(campaign.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleExecuteCampaign(campaign._id)}
                    className="btn btn-outline btn-sm"
                    disabled={campaign.status === 'EXECUTING' || isExecuting}
                  >
                    <PlayIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setShowDeleteDialog(campaign._id)}
                    className="btn btn-error btn-outline btn-sm"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-gray-300 py-12 text-center">
          <RocketLaunchIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            No hay campañas
          </h3>
          <p className="mt-2 text-gray-600">
            Comienza creando tu primera campaña de WhatsApp
          </p>
          <button
            onClick={() => setShowCreateDialog(true)}
            className="btn btn-primary mt-4 rounded-full"
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            Crear Campaña
          </button>
        </div>
      )}

      {/* Create Campaign Modal - DaisyUI Style */}
      <dialog
        id="create_campaign_modal"
        className={`modal ${showCreateDialog ? 'modal-open' : ''}`}
      >
        <div className="pb-safe pt-safe modal-box w-11/12 max-w-2xl">
          <form method="dialog">
            <button
              className="btn btn-ghost btn-sm btn-circle absolute right-2 top-2"
              onClick={() => setShowCreateDialog(false)}
            >
              ✕
            </button>
          </form>

          <h3 className="mb-4 text-lg font-bold">Crear Nueva Campaña</h3>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="label">
                  <span className="label-text">Nombre de la Campaña *</span>
                </label>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="Ej: Promoción Septiembre"
                  className="input-bordered input w-full"
                />
              </div>
            
            </div>

            <div>
              <label className="label">
                <span className="label-text">Descripción (Opcional)</span>
              </label>
              <textarea
                value={campaignDescription}
                onChange={(e) => setCampaignDescription(e.target.value)}
                placeholder="Describe el objetivo de esta campaña..."
                rows={3}
                className="textarea-bordered textarea w-full"
              />
            </div>

            {/* Template Selection: DaisyUI select personalizado */}
            <div>
              <label className="label">
                <span className="label-text">Plantilla *</span>
              </label>
              {approvedTemplates.length > 0 ? (
                <div className="form-control">
                  <select
                    className="select select-bordered w-full"
                    value={selectedTemplate?._id || ''}
                    onChange={(e) => {
                      const id = e.target.value;
                      const found = approvedTemplates.find((t) => t._id === id) || null;
                      setSelectedTemplate(found);
                    }}
                  >
                    <option value="" disabled>
                      Selecciona una plantilla...
                    </option>
                    {approvedTemplates.map((template) => (
                      <option key={template._id} value={template._id}>
                        {`${getTemplateDisplayName(template)} — ${getTemplateLanguage(template)}${
                          (template.variables || template.template?.variables) && (template.variables || template.template?.variables)!.length > 0
                            ? ` (${(template.variables || template.template?.variables)!.length} vars)`
                            : ''
                        }`}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="text-base-content/60 p-4 text-center">
                  No hay plantillas aprobadas disponibles
                </div>
              )}
            </div>

            {/* Audience Selection: DaisyUI select */}
            <div>
              <label className="label">
                <span className="label-text">Audiencia *</span>
              </label>
              {audiences.length > 0 ? (
                <div className="form-control">
                  <select
                    className="select select-bordered w-full"
                    value={selectedAudience?._id || ''}
                    onChange={(e) => {
                      const id = e.target.value;
                      const found = audiences.find((a) => a._id === id) || null;
                      setSelectedAudience(found);
                    }}
                  >
                    <option value="" disabled>
                      Selecciona una audiencia...
                    </option>
                    {audiences.map((audience) => (
                      <option key={audience._id} value={audience._id}>
                        {`${audience.name} — ${audience.estimated_size || 0} contactos`}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="text-base-content/60 p-4 text-center">
                  No hay audiencias disponibles
                </div>
              )}
            </div>

            {/* Batch size selector (Create Campaign) */}
            <div>
              <label className="label">
                <span className="label-text">Enviar por batch</span>
              </label>
              <div className="mb-2">
                <select
                  className="select select-bordered w-full"
                  value={sendBatchSize}
                  onChange={(e) => setSendBatchSize(Number(e.target.value))}
                >
                  <option value={50}>50 por batch</option>
                  <option value={75}>75 por batch</option>
                  <option value={100}>100 por batch</option>
                  <option value={150}>150 por batch</option>
                  <option value={200}>200 por batch</option>
                </select>
              </div>
              <p className="text-sm text-gray-600">
                Selecciona cuántos contactos se enviarán en cada batch. Enviar
                menos contactos por batch ayuda a reducir el riesgo de bloqueos
                por parte de Meta al enviar mensajes en lotes grandes.
              </p>
            </div>
          </div>

          <div className="modal-action">
            <button
              onClick={() => setShowCreateDialog(false)}
              className="btn btn-ghost"
              disabled={isCreating}
            >
              Cancelar
            </button>
            <button
              onClick={handleCreateCampaign}
              className="btn btn-primary rounded-full"
              disabled={
                isCreating ||
                !campaignName.trim() ||
                !selectedTemplate ||
                !selectedAudience
              }
            >
              {isCreating ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Creando...
                </>
              ) : (
                'Crear Campaña'
              )}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowCreateDialog(false)}>close</button>
        </form>
      </dialog>

      {/* Quick Send Modal - DaisyUI Style */}
      <dialog
        id="quick_send_modal"
        className={`modal ${showSendDialog ? 'modal-open' : ''}`}
      >
        <div className="pb-safe pt-safe modal-box w-11/12 max-w-4xl">
          <form method="dialog">
            <button
              className="btn btn-ghost btn-sm btn-circle absolute right-2 top-2"
              onClick={() => {
                setShowSendDialog(null);
                setSelectedTemplate(null);
                setQuickSendContacts([]);
              }}
            >
              ✕
            </button>
          </form>

          <h3 className="mb-4 text-lg font-bold">
            Envío Rápido (Máximo 30 contactos)
          </h3>

          <div className="space-y-6">
            {/* Template Selection: DaisyUI select personalizado (Quick Send) */}
            <div>
              <label className="label">
                <span className="label-text">Plantilla *</span>
              </label>
              {approvedTemplates.length > 0 ? (
                <div className="form-control">
                  <select
                    className="select select-bordered"
                    value={selectedTemplate?._id || ''}
                    onChange={(e) => {
                      const id = e.target.value;
                      const found = approvedTemplates.find((t) => t._id === id) || null;
                      setSelectedTemplate(found);
                    }}
                  >
                    <option value="" disabled>
                      Selecciona una plantilla...
                    </option>
                    {approvedTemplates.map((template) => (
                      <option key={template._id} value={template._id}>
                        {`${getTemplateDisplayName(template)} — ${getTemplateLanguage(template)}${
                          (template.variables || template.template?.variables) && (template.variables || template.template?.variables)!.length > 0
                            ? ` (${(template.variables || template.template?.variables)!.map((v) => v.name).join(', ')})`
                            : ''
                        }`}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="text-base-content/60 p-4 text-center">
                  No hay plantillas aprobadas disponibles
                </div>
              )}
            </div>

            {/* Contacts */}
            {/* Batch size selector (Quick Send) */}
            <div>
              <label className="label">
                <span className="label-text">Enviar por batch</span>
              </label>
              <div className="mb-4">
                <select
                  className="select select-bordered w-full"
                  value={sendBatchSize}
                  onChange={(e) => setSendBatchSize(Number(e.target.value))}
                >
                  <option value={50}>50 por batch</option>
                  <option value={75}>75 por batch</option>
                  <option value={100}>100 por batch</option>
                  <option value={150}>150 por batch</option>
                  <option value={200}>200 por batch</option>
                </select>
              </div>
              <p className="text-sm text-gray-600">
                Selecciona cuántos contactos se enviarán en cada batch. Enviar
                menos contactos por batch ayuda a reducir el riesgo de bloqueos
                por parte de Meta al enviar mensajes en lotes grandes.
              </p>
            </div>
            {selectedTemplate && (
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <label className="label">
                    <span className="label-text">
                      Contactos ({quickSendContacts.length}/30)
                    </span>
                  </label>
                  <button
                    onClick={addQuickSendContact}
                    className="btn btn-outline btn-sm"
                    disabled={quickSendContacts.length >= 30}
                  >
                    <PlusIcon className="mr-1 h-4 w-4" />
                    Agregar Contacto
                  </button>
                </div>

                {quickSendContacts.length > 0 ? (
                  <div className="max-h-80 overflow-y-auto rounded-lg border">
                    <table className="table-sm table w-full">
                      <thead>
                        <tr>
                          <th>Teléfono *</th>
                          {getTemplateVariables(selectedTemplate).map(
                            (variable, index) => (
                              <th key={index}>{variable.name} *</th>
                            ),
                          )}
                          <th>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quickSendContacts.map((contact, contactIndex) => (
                          <tr key={contactIndex}>
                            <td>
                              <input
                                type="tel"
                                value={contact.phone_number}
                                onChange={(e) =>
                                  updateQuickSendContact(
                                    contactIndex,
                                    'phone_number',
                                    e.target.value,
                                  )
                                }
                                placeholder="573001234567"
                                className="input-bordered input-xs input w-full"
                              />
                            </td>
                            {getTemplateVariables(selectedTemplate).map(
                              (variable, variableIndex) => (
                                <td key={variableIndex}>
                                  <input
                                    type="text"
                                    value={
                                      contact.variables[variableIndex] || ''
                                    }
                                    onChange={(e) =>
                                      updateContactVariable(
                                        contactIndex,
                                        variableIndex,
                                        e.target.value,
                                      )
                                    }
                                    placeholder={variable.example}
                                    className="input-bordered input-xs input w-full"
                                  />
                                </td>
                              ),
                            )}
                            <td>
                              <button
                                onClick={() =>
                                  removeQuickSendContact(contactIndex)
                                }
                                className="btn btn-ghost btn-xs text-error"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="border-base-300 rounded-lg border border-dashed py-8 text-center">
                    <p className="text-base-content/60 text-sm">
                      No hay contactos. Agrega contactos para enviar la campaña.
                    </p>
                  </div>
                )}

                {quickSendContacts.length >= 30 && (
                  <div className="alert alert-warning">
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    <span>
                      Has alcanzado el límite máximo de 30 contactos por envío
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-action">
            <button
              onClick={() => {
                setShowSendDialog(null);
                setSelectedTemplate(null);
                setQuickSendContacts([]);
              }}
              className="btn btn-ghost"
              disabled={isSending}
            >
              Cancelar
            </button>
            <button
              onClick={handleQuickSend}
              className="btn btn-primary rounded-full"
              disabled={
                isSending ||
                !selectedTemplate ||
                quickSendContacts.filter((c) => c.phone_number.trim())
                  .length === 0
              }
            >
              {isSending ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Enviando...
                </>
              ) : (
                `Enviar a ${
                  quickSendContacts.filter((c) => c.phone_number.trim()).length
                } contactos`
              )}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button
            onClick={() => {
              setShowSendDialog(null);
              setSelectedTemplate(null);
              setQuickSendContacts([]);
            }}
          >
            close
          </button>
        </form>
      </dialog>

      {/* Delete Confirmation Modal - DaisyUI Style */}
      <dialog
        id="delete_campaign_modal"
        className={`modal ${showDeleteDialog ? 'modal-open' : ''}`}
      >
        <div className="pb-safe pt-safe modal-box">
          <form method="dialog">
            <button
              className="btn btn-ghost btn-sm btn-circle absolute right-2 top-2"
              onClick={() => setShowDeleteDialog(null)}
            >
              ✕
            </button>
          </form>

          <h3 className="text-lg font-bold">Confirmar Eliminación</h3>
          <p className="py-4">
            ¿Estás seguro de que quieres eliminar esta campaña? Esta acción no
            se puede deshacer.
          </p>

          <div className="modal-action">
            <button
              onClick={() => setShowDeleteDialog(null)}
              className="btn btn-ghost"
              disabled={isDeleting}
            >
              Cancelar
            </button>
            <button
              onClick={() =>
                showDeleteDialog && handleDeleteCampaign(showDeleteDialog)
              }
              className="btn btn-error"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Eliminando...
                </>
              ) : (
                'Eliminar'
              )}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowDeleteDialog(null)}>close</button>
        </form>
      </dialog>

      {/* Send Campaign Confirmation Modal - DaisyUI Style */}
      <dialog
        id="send_confirmation_modal"
        className={`modal ${showSendConfirmation ? 'modal-open' : ''}`}
      >
        <div className="pb-safe pt-safe modal-box">
          <form method="dialog">
            <button
              className="btn btn-ghost btn-sm btn-circle absolute right-2 top-2"
              onClick={() => setShowSendConfirmation(false)}
            >
              ✕
            </button>
          </form>

          <h3 className="text-lg font-bold">Confirmar Envío de Campaña</h3>
          
          {selectedTemplate && quickSendContacts && (
            <div className="py-4 space-y-3">
              <div className="bg-base-100 rounded-lg p-4 border">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Campaña:</strong> Envío Rápido
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Plantilla:</strong> {getTemplateDisplayName(selectedTemplate)}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Contactos:</strong> {quickSendContacts.filter((c) => c.phone_number.trim()).length} registros
                </p>
              </div>
              
              <div className="alert alert-warning">
                <ExclamationTriangleIcon className="h-5 w-5" />
                <span>
                  ¿Estás seguro de que quieres enviar esta campaña? Una vez enviada no se puede cancelar.
                </span>
              </div>
            </div>
          )}

          <div className="modal-action">
            <button
              onClick={() => setShowSendConfirmation(false)}
              className="btn btn-ghost"
              disabled={isSending}
            >
              Cancelar
            </button>
            <button
              onClick={confirmSendCampaign}
              className="btn btn-primary rounded-full"
              disabled={isSending}
            >
              {isSending ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Enviando...
                </>
              ) : (
                'Confirmar y Enviar'
              )}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowSendConfirmation(false)}>close</button>
        </form>
      </dialog>

      {/* Execute Campaign Confirmation Modal - DaisyUI Style */}
      <dialog
        id="execute_confirmation_modal"
        className={`modal ${showExecuteConfirmation ? 'modal-open' : ''}`}
      >
        <div className="pb-safe pt-safe modal-box">
          <form method="dialog">
            <button
              className="btn btn-ghost btn-sm btn-circle absolute right-2 top-2"
              onClick={() => setShowExecuteConfirmation(null)}
            >
              ✕
            </button>
          </form>

          <h3 className="text-lg font-bold">Confirmar Ejecución de Campaña</h3>
          
          {showExecuteConfirmation && (
            <div className="py-4 space-y-3">
              {(() => {
                const campaign = campaigns.find(c => c._id === showExecuteConfirmation);
                return campaign ? (
                  <div className="bg-base-100 rounded-lg p-4 border">
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Campaña:</strong> {campaign.name}
                    </p>
                    {campaign.description && (
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Descripción:</strong> {campaign.description}
                      </p>
                    )}
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Tipo:</strong> {campaign.type}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Plantillas:</strong> {campaign.templates.map(t => t.template_name).join(', ')}
                    </p>
                    {campaign.metrics && (
                      <p className="text-sm text-gray-600">
                        <strong>Destinatarios:</strong> {campaign.metrics.total_recipients} contactos
                      </p>
                    )}
                  </div>
                ) : null;
              })()}
              
              <div className="alert alert-warning">
                <ExclamationTriangleIcon className="h-5 w-5" />
                <span>
                  ¿Estás seguro de que quieres ejecutar esta campaña? Una vez ejecutada se enviará a todos los destinatarios configurados.
                </span>
              </div>
            </div>
          )}

          <div className="modal-action">
            <button
              onClick={() => setShowExecuteConfirmation(null)}
              className="btn btn-ghost"
              disabled={isExecuting}
            >
              Cancelar
            </button>
            <button
              onClick={confirmExecuteCampaign}
              className="btn btn-primary rounded-full"
              disabled={isExecuting}
            >
              {isExecuting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Ejecutando...
                </>
              ) : (
                'Confirmar y Ejecutar'
              )}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowExecuteConfirmation(null)}>close</button>
        </form>
      </dialog>
    </div>
  );
};
